<template>
  <!-- Deleted state: only shown once the theme is confirmed missing. -->
  <div
    v-if="isNotFound"
    class="playlist-layer playlist-item deleted"
    :class="{ active: isCurrentLayout || isCurrentSampleLayout }"
  >
    <div class="layer-info">
      <div class="deleted-message">
        This Theme <span v-if="displayTitle">({{ displayTitle }})</span> has been deleted.
      </div>
      <button
        class="tb-btn tb-btn-text sm medium-em ml-1"
        title="Remove item from playlist"
        @click.stop.prevent="removeItem"
      >
        <i class="fas fa-minus-circle"></i>
      </button>
    </div>
  </div>

  <div
    v-else-if="theme"
    :data-test="`theme:${theme._id}`"
    class="playlist-layer playlist-item theme"
    :class="[{ 'empty': !posts.length }, { 'editing': isEditing }]"
    @click="onClickTimelineItem"
  >
    <div class="layer-info">
      <button
        class="drag-handle tb-btn tb-btn-link sm low-em"
        title="Rearrange"
      >
        <span class="fas fa-grip-vertical" />
      </button>

      <div class="layer-title flex-grow ml-2">
        <div class="flex-grow ellipsis">
          <div class="title-text">{{ displayTitle }}</div>
          <div class="description-text capitalize">{{ theme.layout }}</div>
        </div>
      </div>

      <!-- Live switch toggle -->
      <preview-live-switch :item="item" @live-clicked="onLiveSwitchClick" />

      <dropdown position="left" class="more-actions ml-1">
        <template #trigger>
          <button class="tb-btn tb-btn-text sm medium-em" title="More actions">
            <i class="fas fa-ellipsis-v"></i>
          </button>
        </template>

        <template #dropdown-body>
          <button class="dropdown-list-item" @click="duplicateItem">
            <span class="dropdown-list-item-label">Duplicate in Playlist</span>
          </button>

          <button v-if="canDuplicateInGroup" class="dropdown-list-item" @click="duplicateItemInGroup">
            <span class="dropdown-list-item-label">Duplicate in Segment</span>
          </button>

          <button
            v-if="canMergeUp"
            class="dropdown-list-item"
            @click="mergeUp"
            title="Merge with above item in the playlist"
          >
            <span class="dropdown-list-item-label">Merge Up</span>
          </button>

          <button
            v-if="canMergeDown"
            class="dropdown-list-item"
            @click="mergeDown"
            title="Merge with below item in the playlist"
          >
            <span class="dropdown-list-item-label">Merge Down</span>
          </button>

          <hr />

          <button v-if="posts.length" class="dropdown-list-item" @click="clearPosts">
            <span class="dropdown-list-item-label warning">Clear Posts</span>
          </button>

          <button class="dropdown-list-item" @click="removeItem">
            <span class="dropdown-list-item-label warning">Remove</span>
          </button>
        </template>
      </dropdown>
    </div>

    <div class="layer-rundown">
      <draggable
        ref="tgbDropArea"
        class="layer-draggable draggable tgbPostDrop tgbTagboardDrop tgbDropArea scroll-x"
        @change="onChange"
        @start="onStart"
        :move="checkMove"
        @unchoose="onUnchoose"
        v-model="posts"
        revert-on-spill="true"
        v-bind="{
          group: { name: 'posts', put: check },
          animation: 200,
          touchStartThreshold: 10,
        }"
        data-test="producer-socialDisplay-dropzone"
      >
        <component
          :is="getType(post)"
          v-for="(post, index) in posts"
          :key="index"
          :layout="item"
          :item="post"
          :parent="item"
          :queuedPost="queuedPost"
          @select="selectThemePost"
          @delete="removeThemePost"
          @play-video="playVideo"
          @toggle-video="togglePlaying"
          @play-preview-video="playVideo(true)"
          @toggle-preview-video="togglePlaying(true)"
        />

        <div class="tgbDropAreaHelp" v-if="!posts.length">
          <p>Drag and drop a Board or posts here</p>
        </div>
      </draggable>
    </div>
  </div>

  <!-- Loading state: theme metadata hasn't loaded yet, and we
       haven't confirmed the theme is missing either. -->
  <div
    v-else
    class="playlist-layer playlist-item flex items-center"
    :class="{ active: isCurrentLayout || isCurrentSampleLayout }"
  >
    <div class="playlist-layer__skeleton" />

    <div class="layer-info">
      <button
        class="tb-btn tb-btn-text sm medium-em ml-1"
        title="Remove item from playlist"
        @click.stop.prevent="removeItem"
      >
        <i class="fas fa-minus-circle"></i>
      </button>
    </div>
  </div>
</template>

<script>
import debounce from 'lodash/debounce';
import TimelineMixins from '../../../mixins/timeline-mixins';
import TimelinePostComponent from '../post/TimelinePost.vue';
import TimelinePostPanelComponent from '../post-panel/TimelinePostPanel.vue';
import PreviewLiveSwitch from '../../PreviewLiveSwitch.vue';

export default {
  name: 'TimelineLayoutComponent',
  mixins: [TimelineMixins],
  props: ['item'],

  components: {
    post: TimelinePostComponent,
    panel: TimelinePostPanelComponent,
    'preview-live-switch': PreviewLiveSwitch,
  },

  data() {
    return {
      error: false,
      lastTrackedQueuedPost: null,
      lastPostClickId: null,
    };
  },

  watch: {
    queuedPost(newVal, oldVal) {
      if (this.isTrackableTheme && newVal !== oldVal) {
        this.debouncedQueuedPostHandler(newVal, oldVal);
      }
    },
  },

  computed: {
    production() {
      return this.$store.state.production.production;
    },

    posts: {
      get() {
        return this.item.posts;
      },
      set(posts) {
        this.$store.dispatch('setPosts', {
          posts,
          timelineId: this.item.timeline_id,
        });
      },
    },

    queuedPost() {
      return this.item.queuedPost;
    },

    theme() {
      return this.$store.state.themes.lookupTable[this.item._id];
    },

    /**
     * Whether the theme metadata is still loading.
     */
    isLoading() {
      return !!this.$store.state.themes.isLoadingMap[this.item._id];
    },

    /**
     * Whether the theme has been confirmed missing (deleted).
     */
    isNotFound() {
      return !!this.$store.state.themes.notFoundMap[this.item._id];
    },

    displayTitle() {
      return this.item?.title || this.theme?.title || '';
    },

    isTrackableTheme() {
      return ['isolated', 'cinematic', 'lowerthirdscroll', 'lowerthird'].includes(this.item?.layout);
    },

    isPlaying() {
      return this.production.playing;
    },
  },

  methods: {
    playVideo(preview) {
      this.$store.dispatch(preview ? 'playPreviewVideo' : 'playPostVideo');
    },
    togglePlaying(preview) {
      this.$store.dispatch(preview ? 'togglePreviewVideo' : 'toggleLiveVideo');
    },
    getType(item) {
      return item?.layout === 'panel' ? 'panel' : 'post';
    },
    clearPosts() {
      this.posts = [];
    },
    onStart() {
      document.querySelectorAll('.tgbTagboardDrop,.tgbPostDrop').forEach(el => el.classList.add('tgbDropArea--active'));
    },
    checkMove(e) {
      if (this.$refs.tgbDropArea.$el === e.to.closest('.tgbDropArea')) return true;
      const { element } = e.draggedContext || {};
      if (!element) return false;
      const { type, post_id: postId } = element;
      const hasContent = Boolean(e.relatedContext.list.length);
      const isInList = e.relatedContext.list.some(i => i?.post_id === postId);
      const notAllowed = (hasContent && type === 'tagboard') || isInList;
      const errorMessages = {
        tagboard: 'A full Board cannot be presented with other content.',
        duplicate: 'Duplicate posts not allowed.',
      };
      if (notAllowed) {
        e.to.classList.add('tgbDropArea--no');
        e.dragged.style.cursor = 'not-allowed';
        this.error = errorMessages[(hasContent && type === 'tagboard') ? 'tagboard' : 'duplicate'];
        return false;
      }

      this.error = false;
      return true;
    },
    onUnchoose(e) {
      e.item.style.cursor = 'pointer';
      if (this.error) {
        this.errorToast(this.error);
      }

      document.querySelectorAll('.tgbDropArea--no, .tgbDropArea--active').forEach(el => el.classList.remove('tgbDropArea--no', 'tgbDropArea--active'));
      this.error = false;
    },
    onChange(e) {
      if (!e || !e.added){
        return;
      }

      this.$store.dispatch('handleTagboardDrop', {
        item: this.item,
        e,
      });
    },
    check(to, from, el) {
      if (["tagboard", "posts", "socialGraphic"].includes(from.options.group.name)) {
        const post = el.classList.contains('grid-cell') ? el.querySelector('.post-wrapper') : el;
        if (this.item.layout === 'stories') {
          switch (this.theme.settings.network) {
            case 'snapchat': return post?.classList.contains('snapchat');
            case 'instagram': return post?.classList.contains('instagram-stories');
            default: return false;
          }
        } else {
          return !post?.classList.contains('snapchat') && !post?.classList.contains('instagram-stories');
        }
      }

      if (from.options.group.name === 'timeline') {
        return ['source-item', 'smart-panel'].every((c) => el.classList.contains(c));
      }

      return false;
    },
    onLiveSwitchClick() {
      if (!this.isTrackableTheme || this.posts.some(p => p.type === 'tagboard')) return;
      const post = this.posts.find(p => p.timeline_id === this.queuedPost);
      const isActivating = !this.isCurrentLayout;
      const value = isActivating ? 'on' : 'off';
      if (post) {
        this.$store.dispatch('trackLivePostEvent', {
          item: post,
          eventSource: 'element',
          value,
        });

        if (isActivating) this.lastTrackedQueuedPost = post.timeline_id;
      }
    },
    handlePostClick(postId) {
      this.lastPostClickId = postId;
    },
    selectThemePost(opts = {}) {
      const {
        item,
        parent: parentTimelineId,
      } = opts;
      this.lastPostClickId = item.timeline_id;

      const payload = { parentTimelineId, item, eventSource: 'click' };
      const isClickedWhilePlaying = this.isPlaying && this.isCurrentLayout;
      if (isClickedWhilePlaying) {
        payload.eventSource = 'auto-advance'; // if actively playing then the source should always be auto-advance.
        return;
      }

      this.$store.dispatch('toggleLivePost', payload);
      this.$store.dispatch('togglePreviewPost', payload);
    },
    queuedPostHandler(newVal, oldVal) {
      if (!this.isCurrentLayout || newVal === oldVal) return;

      const value = newVal ? 'on' : 'off';
      const postId = newVal || oldVal;
      const source = 'auto-advance';

      if (this.lastPostClickId === postId) {
        return; // selectThemePost already processed this click. This is for auto-advance and 'element' sources.
      }

      const post = this.posts.find(p => p.timeline_id === postId);
      if (post) {
        this.$store.dispatch('trackLivePostEvent', {
          item: post,
          eventSource: source,
          value,
        });

        this.lastTrackedQueuedPost = post.timeline_id;
      }
    },
  },

  created() {
    this.debouncedQueuedPostHandler = debounce(this.queuedPostHandler, 300, {
      leading: true,
      trailing: false,
    });
  },

  beforeUnmount() {
    if (this.debouncedQueuedPostHandler && this.debouncedQueuedPostHandler.cancel) {
      this.debouncedQueuedPostHandler.cancel();
    }
  },
};
</script>
